'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Users, Megaphone, Bot, ChevronLeft, ChevronRight, ExternalLink, Zap,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const navItems = [
  {
    key: 'nav-dashboard',
    label: 'Dashboard',
    href: '/',
    icon: LayoutDashboard,
    badge: null,
  },
  {
    key: 'nav-leads',
    label: 'Leads',
    href: '/lead-management',
    icon: Users,
    badge: '47',
  },
  {
    key: 'nav-campaigns',
    label: 'Campañas',
    href: '/campaign-funnel-management',
    icon: Megaphone,
    badge: '3',
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`relative flex flex-col bg-card border-r border-border sidebar-transition shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      style={{ minHeight: '100vh' }}
    >
      {/* Logo */}
      <div
        className={`flex items-center gap-3 px-4 py-5 border-b border-border ${
          collapsed ? 'justify-center px-2' : ''
        }`}
      >
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-sm text-foreground leading-tight truncate">
              MarketingMF
            </span>
            <span className="text-2xs text-muted-foreground truncate">
              Motor Geo · IA
            </span>
          </div>
        )}
      </div>
      {/* Bot status indicator */}
      {!collapsed && (
        <div className="mx-3 mt-3 mb-1 px-3 py-2 rounded-lg bg-accent/10 border border-accent/20 flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="status-pulse-green absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
          </span>
          <span className="text-2xs font-medium text-accent">
            3/3 Bots activos
          </span>
          <Zap size={10} className="text-accent ml-auto" />
        </div>
      )}
      {/* Nav */}
      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto scrollbar-thin">
        {navItems?.map((item) => {
          const Icon = item?.icon;
          const isActive =
            item?.href === '/'
              ? pathname === '/'
              : pathname?.startsWith(item?.href);

          return (
            <Link
              key={item?.key}
              href={item?.href}
              title={collapsed ? item?.label : undefined}
              className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 ${
                isActive
                  ? 'bg-primary/15 text-primary' :'text-muted-foreground hover:bg-secondary hover:text-foreground'
              } ${collapsed ? 'justify-center' : ''}`}
            >
              {isActive && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r-full" />
              )}
              <Icon
                size={18}
                className={`shrink-0 ${isActive ? 'text-primary' : ''}`}
              />
              {!collapsed && (
                <>
                  <span className="text-sm font-medium flex-1 truncate">
                    {item?.label}
                  </span>
                  {item?.badge && (
                    <span
                      className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${
                        isActive
                          ? 'bg-primary/20 text-primary' :'bg-secondary text-muted-foreground'
                      }`}
                    >
                      {item?.badge}
                    </span>
                  )}
                </>
              )}
              {/* Tooltip for collapsed */}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 rounded bg-elevated border border-border text-xs text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  {item?.label}
                  {item?.badge && (
                    <span className="ml-1 text-primary">({item?.badge})</span>
                  )}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      {/* External link */}
      <div className={`px-2 pb-2 ${collapsed ? 'px-2' : ''}`}>
        <a
          href="https://myfindermf.base44.app"
          target="_blank"
          rel="noopener noreferrer"
          className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-150 ${
            collapsed ? 'justify-center' : ''
          }`}
          title={collapsed ? 'Ver Myfinder MF' : undefined}
        >
          <ExternalLink size={16} className="shrink-0" />
          {!collapsed && (
            <span className="text-xs font-medium truncate">Ver Myfinder MF</span>
          )}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 rounded bg-elevated border border-border text-xs text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              Ver Myfinder MF
            </span>
          )}
        </a>
      </div>
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary transition-all duration-150"
        aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}